/**
 * Договір оферти — внутрішня сторінка Mini App (без exit на browser).
 * Контент — `src/data/offerta.json`. ⚠️ Це ДЖЕРЕЛО ІСТИНИ: договір правиться
 * прямо в цьому файлі (owner 2026-08-12). Сторінка на teletype і скрипт
 * `scripts/fetch_offerta.py` більше НЕ джерело — повторний «синк» затре
 * актуальний текст.
 *
 * Стиль 1:1 з KBArticle.
 */

import { Link } from "react-router-dom";
import { BackNav } from "@/components/BackNav";
import { Icon } from "@/components/Icon";
import offertaData from "@/data/offerta.json";

// Дата останнього оновлення договору (юзер 2026-06-25). Оновлювати вручну
// при кожній зміні тексту оферти.
const OFFERTA_UPDATED_AT = "22 серпня 2026";

interface Node {
  type: string;
  value?: string;
  href?: string;
  children?: Node[];
}

interface OffertaData {
  title: string;
  source_url: string;
  content: Node[];
}

const data = offertaData as OffertaData;

function renderNode(node: Node, key: string): React.ReactNode {
  if (node.type === "text") return node.value ?? "";
  const kids = (node.children ?? []).map((c, i) =>
    renderNode(c, `${key}-${i}`),
  );
  switch (node.type) {
    case "h1":
      return (
        <h2
          key={key}
          className="mt-7 mb-2 font-display text-[1.375rem] font-bold text-ink leading-tight"
          style={{ letterSpacing: "-0.02em" }}
        >
          {kids}
        </h2>
      );
    case "h2":
      return (
        <h2
          key={key}
          className="mt-6 mb-2 font-display text-[1.125rem] font-bold text-ink leading-tight"
          style={{ letterSpacing: "-0.02em" }}
        >
          {kids}
        </h2>
      );
    case "h3":
      return (
        <h3
          key={key}
          className="mt-5 mb-1.5 font-display text-[0.9375rem] font-semibold uppercase tracking-[0.04em] text-ink"
        >
          {kids}
        </h3>
      );
    case "h4":
      return (
        <h4 key={key} className="mt-4 mb-1 font-display text-[0.875rem] font-semibold text-ink">
          {kids}
        </h4>
      );
    case "p":
      return (
        <p key={key} className="my-2.5 text-[0.875rem] leading-relaxed text-ink">
          {kids}
        </p>
      );
    case "ul":
      return (
        <ul key={key} className="my-3 ml-5 list-disc space-y-1.5 text-[0.875rem] leading-relaxed text-ink marker:text-primary">
          {kids}
        </ul>
      );
    case "ol":
      return (
        <ol key={key} className="my-3 ml-5 list-decimal space-y-1.5 text-[0.875rem] leading-relaxed text-ink marker:text-primary marker:font-semibold">
          {kids}
        </ol>
      );
    case "li":
      return <li key={key}>{kids}</li>;
    case "b":
      return <b key={key} className="font-semibold text-ink">{kids}</b>;
    case "i":
      return <i key={key} className="italic">{kids}</i>;
    case "a":
      return (
        <a
          key={key}
          href={node.href || "#"}
          target="_blank"
          rel="noopener noreferrer"
          className="text-primary underline underline-offset-2 hover:no-underline"
        >
          {kids}
        </a>
      );
    default:
      return <span key={key}>{kids}</span>;
  }
}

export function OffertaPage() {
  return (
    <div className="-mx-5 -mt-5 min-h-screen pb-16">
      <div className="px-5 pt-5">
        <BackNav />
      </div>

      {/* Hero */}
      <div className="px-5 pt-4">
        <div className="font-mono text-[0.65625rem] uppercase tracking-[0.14em] text-ink-dim mb-2">
          Юридична інформація
        </div>
        <h1
          className="font-display font-bold text-ink"
          style={{ fontSize: "1.875rem", lineHeight: 1.05, letterSpacing: "-0.025em" }}
        >
          {data.title}
        </h1>
        {/* Дата останнього оновлення — тонким шрифтом під заголовком */}
        <div className="mt-2 text-[0.75rem] font-normal text-ink-dim">
          Останнє оновлення: {OFFERTA_UPDATED_AT}
        </div>
      </div>

      {/* Body */}
      <article className="px-5 pt-2 pb-6">
        {data.content.map((n, i) => renderNode(n, `n-${i}`))}
      </article>

      {/* Повернутися до меню — внизу сторінки (юзер 2026-06-25) */}
      <div className="px-5 pb-16">
        <Link
          to="/"
          className="flex w-full items-center justify-center gap-2 rounded-full bg-surface-soft py-3 font-display text-[0.8125rem] font-semibold text-ink shadow-card-soft active:scale-[0.98] transition"
        >
          Повернутися до меню
          <Icon name="home" size={14} color="#FE6B04" />
        </Link>
      </div>
    </div>
  );
}
